/**
 * =============================================================================
 * Página: ReferralAffiliates
 * Versão: 3.1.0
 *
 * Descrição:
 *   Listagem e moderação de afiliados do tenant ativo, padronizada com os
 *   helpers de layout reutilizáveis e agora 100% server-side via RPC
 *   `get_referral_affiliates_paginated`.
 *
 *   - Header compacto + 4 KPI cards (Total / Pendentes / Ativos / Saldo).
 *   - Filtros (busca, status, programa, faixa de cadastro, somente com saldo)
 *     são processados no banco — zero filtragem client-side.
 *   - Paginação, total_count e KPIs vêm em uma única chamada à RPC
 *     (espelha Comissões e Pagamentos do mesmo módulo).
 *
 * Release notes:
 *   v1.0.0 - Versão inicial (header customizado, sem KPIs, sem helpers).
 *   v2.0.0 - Padronização total com helpers `list-page` + KPIs + Sheet "Mais".
 *   v3.0.0 - Migração para paginação server-side via RPC. Remove paginação
 *            client-side e filtros locais. KPIs agora vêm do servidor.
 *   v3.1.1 - Realtime para refletir cadastros novos sem F5.
 *   v3.2.0 - Modo "embedded" para reaproveitamento dentro do detalhe do
 *            programa (`ReferralProgramDetail`). Quando `embedded`:
 *              - Esconde o cabeçalho próprio.
 *              - Trava o filtro por programa (oculta o seletor).
 *              - Pré-seleciona o programa no modal de cadastro.
 *   v3.3.0 - Scroll infinito no mobile (server-side, 20 por página) reusando
 *            a mesma RPC. Desktop continua com paginação tradicional.
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
// v3.0.0: paginação 100% server-side via RPC get_referral_affiliates_paginated
import { useReferralAffiliatesPaginated } from '@/hooks/useReferralAffiliatesPaginated';
// v3.3.0: scroll infinito server-side para mobile (mesma RPC, useInfiniteQuery)
import { useReferralAffiliatesInfinite } from '@/hooks/useReferralAffiliatesInfinite';
import { useIsMobile } from '@/hooks/use-mobile';
import { InfiniteScrollSentinel } from '@/components/ui/infinite-scroll-sentinel';
// v3.1.0: Realtime para refletir cadastros novos e provisionamento lazy de links sem F5
import { useReferralAffiliatesRealtime } from '@/hooks/useReferralAffiliatesRealtime';
import { useAuth } from '@/hooks/useAuth';
import {
  Users,
  Search,
  CheckCircle2,
  Clock,
  Wallet,
  RefreshCw,
  CircleDot,
  Award,
  Calendar as CalendarIcon,
  Plus,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { type AffiliateStatus } from '@/hooks/useReferralAffiliates';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import { ReferralAffiliateDetailSheet } from '@/components/referral/ReferralAffiliateDetailSheet';
import { AffiliateAdminCreateModal } from '@/components/referral/AffiliateAdminCreateModal';
import { ReferralQuotaInline } from '@/components/referral/ReferralQuotaInline';
import { ReferralUsageInline } from '@/components/referral/billing/ReferralUsageInline';
import { ImportAffiliatesWizard } from '@/components/referral/import/ImportAffiliatesWizard';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { AffiliatesTableView } from '@/components/referral/AffiliatesTableView';
import { usePermissions } from '@/hooks/usePermissions';
// Helpers de layout padronizado (paridade visual com CrmDeals/Comissões)
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from '@/components/layout/list-page';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { cn } from '@/lib/utils';

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/** Formata valor monetário em BRL. */
function formatBRL(value: number | null | undefined) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

/** Cor da bolinha indicadora por status (usada no Select inline). */
const STATUS_DOT: Record<AffiliateStatus, string> = {
  pending:   'bg-yellow-500',
  active:    'bg-green-500',
  suspended: 'bg-orange-500',
  rejected:  'bg-red-500',
  inactive:  'bg-muted-foreground/40',
};

const STATUS_LABEL: Record<AffiliateStatus, string> = {
  pending: 'Pendente',
  active: 'Ativo',
  suspended: 'Suspenso',
  rejected: 'Reprovado',
  inactive: 'Inativo',
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

// =============================================================================
// Props (v3.2.0): suportar modo "embedded" dentro do detalhe do programa
// =============================================================================
interface ReferralAffiliatesProps {
  /** Quando setado, trava o filtro de programa (oculta o seletor). */
  embeddedProgramId?: string;
  /** Quando true, oculta o cabeçalho da página (drill-down). */
  embedded?: boolean;
}

export default function ReferralAffiliates({
  embeddedProgramId,
  embedded = false,
}: ReferralAffiliatesProps = {}) {
  // =========================================================================
  // Estados de filtro (rápidos)
  // =========================================================================
  const [status, setStatus] = useState<AffiliateStatus | 'all'>('all');
  // Em modo embedded, o programa fica fixo no contexto da página de detalhe.
  const [programId, setProgramId] = useState<string | 'all'>(
    embeddedProgramId ?? 'all',
  );
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Filtros avançados (Sheet "Mais")
  const [onlyWithBalance, setOnlyWithBalance] = useState(false);
  const [createdFrom, setCreatedFrom] = useState<string>('');
  const [createdTo, setCreatedTo] = useState<string>('');
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  // Drawer de detalhes
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Modal de criação administrativa
  const [createOpen, setCreateOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setCreateOpen(true));
  const [importOpen, setImportOpen] = useState(false);

  // Permissões (controla visibilidade do botão "Novo Afiliado")
  const { canCreate } = usePermissions();
  const canCreateAffiliate = canCreate('referral_affiliates');

  // Paginação client-side (limit do hook = 500)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // v3.3.0: detecta mobile (<768px) para alternar entre paginação e infinite
  const isMobile = useIsMobile();

  // v3.1.0: Realtime — invalida a query da listagem ao receber INSERT/UPDATE
  // em `affiliates` ou `affiliate_links` do tenant ativo (corrige coluna "Link"
  // não atualizar após cadastro novo).
  const { currentTenant } = useAuth();
  useReferralAffiliatesRealtime(currentTenant?.id ?? null);

  // =========================================================================
  // Hooks de dados
  // =========================================================================
  const { data: programs = [] } = useReferralPrograms();

  // Debounce da busca textual (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // =========================================================================
  // Filtros consolidados (idênticos para os dois hooks)
  // =========================================================================
  const rpcFilters = useMemo(
    () => ({
      status: status === 'all' ? null : status,
      // v3.2.0: em modo embedded, força o programa do contexto.
      program_id: embedded
        ? embeddedProgramId ?? null
        : programId === 'all'
          ? null
          : programId,
      search: debouncedSearch || null,
      only_with_balance: onlyWithBalance,
      created_from: createdFrom || null,
      created_to: createdTo || null,
      // Ordenação padrão por PERFORMANCE composta:
      // Indicações DESC > Conversões DESC > Cliques DESC.
      // Mantém afiliados de maior produção no topo da listagem.
      sort_by: 'performance',
      sort_dir: 'desc' as const,
    }),
    [
      status,
      embedded,
      embeddedProgramId,
      programId,
      debouncedSearch,
      onlyWithBalance,
      createdFrom,
      createdTo,
    ],
  );

  // =========================================================================
  // v3.0.0: Listagem 100% server-side via RPC paginada (DESKTOP)
  // v3.3.0: desativada quando estiver em mobile (economiza network/cache)
  // =========================================================================
  const paginatedQuery = useReferralAffiliatesPaginated(
    rpcFilters,
    { page: currentPage, pageSize },
  );

  // =========================================================================
  // v3.3.0: Listagem com scroll infinito server-side (MOBILE)
  // Mesma RPC, useInfiniteQuery, 20 itens por página.
  // =========================================================================
  const infiniteQuery = useReferralAffiliatesInfinite(rpcFilters, {
    pageSize: 20,
    enabled: isMobile,
  });

  // Seleciona a fonte de dados conforme o breakpoint
  const paginatedAffiliates = isMobile
    ? infiniteQuery.affiliates
    : paginatedQuery.affiliates;
  const totalCount = isMobile ? infiniteQuery.totalCount : paginatedQuery.totalCount;
  const kpis = isMobile ? infiniteQuery.kpis : paginatedQuery.kpis;
  const isLoading = isMobile ? infiniteQuery.isLoading : paginatedQuery.isLoading;
  const isFetching = isMobile ? infiniteQuery.isFetching : paginatedQuery.isFetching;
  const error = isMobile ? infiniteQuery.error : paginatedQuery.error;
  const refetch = isMobile ? infiniteQuery.refetch : paginatedQuery.refetch;

  const isError = !!error;

  const { isRefreshing, triggerRefresh } = useRefreshWithTimestamp();

  // Reseta página ao mudar filtros (server-side recalcula tudo)
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearch,
    status,
    programId,
    onlyWithBalance,
    createdFrom,
    createdTo,
  ]);

  // =========================================================================
  // Handlers
  // =========================================================================
  const openDetails = (id: string) => {
    setSelectedId(id);
    setSheetOpen(true);
  };

  const handleRefresh = () => {
    triggerRefresh(async () => {
      await refetch();
    });
  };

  /** Contador de filtros avançados ativos (badge do botão "Mais"). */
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    if (onlyWithBalance) count++;
    if (createdFrom || createdTo) count++;
    return count;
  }, [onlyWithBalance, createdFrom, createdTo]);

  const clearAdvancedFilters = () => {
    setOnlyWithBalance(false);
    setCreatedFrom('');
    setCreatedTo('');
  };

  // =========================================================================
  // Render
  // =========================================================================
  return (
    <ListPageLayout>
      {/* ================================================================ */}
      {/* Header padronizado (helper) — oculto em modo embedded             */}
      {/* ================================================================ */}
      {!embedded && (
        <ListPageHeader
          title="Afiliados"
          description="Aprove cadastros, modere status e acompanhe performance individual."
          actions={
            // Padrão TaskList: indicadores de uso (acima) + botões de ação (abaixo)
            <div className="flex flex-col items-end gap-1.5">
              <ReferralUsageInline className="hidden md:inline-flex" />
              <div className="flex items-center gap-1.5">
                {canCreateAffiliate && (
                  <>
                    {/* Importar Afiliados em Massa (CSV) — redireciona à Central de Listas */}
                    <Button
                      asChild
                      variant="outline"
                      size="icon-sm"
                      title="Importar Afiliados em Massa (CSV)"
                    >
                      <Link to="/tenant/lists?tab=affiliates">
                        <Upload className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                    <ReferralQuotaInline resource="affiliates">
                      <Button size="xs" onClick={() => setCreateOpen(true)}>
                        <Plus className="h-3.5 w-3.5" />
                        Novo Afiliado
                      </Button>
                    </ReferralQuotaInline>
                  </>
                )}
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  title="Atualizar dados"
                >
                  <RefreshCw
                    className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')}
                  />
                </Button>
              </div>
            </div>
          }
        />
      )}

      {/* Em modo embedded, mostra apenas as ações alinhadas à direita */}
      {embedded && (
        <div className="flex justify-end gap-2 px-4 pt-3">
          {canCreateAffiliate && (
            <>
              <Button
                asChild
                variant="outline"
                size="icon-sm"
                title="Importar Afiliados em Massa (CSV)"
              >
                <Link to="/tenant/lists?tab=affiliates">
                  <Upload className="h-3.5 w-3.5" />
                </Link>
              </Button>
              <ReferralQuotaInline resource="affiliates">
                <Button size="xs" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  Novo Afiliado
                </Button>
              </ReferralQuotaInline>
            </>
          )}
          <Button
            variant="outline"
            size="icon-sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Atualizar dados"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')}
            />
          </Button>
        </div>
      )}

      {/* ================================================================ */}
      {/* KPIs padronizados (helper)                                        */}
      {/* ================================================================ */}
      {!isLoading && (
        <ListPageKpis>
          <KpiCard
            color="purple"
            icon={Users}
            label="Total"
            value={kpis.total}
          />
          <KpiCard
            color="yellow"
            icon={Clock}
            label="Pendentes"
            value={kpis.pending}
          />
          <KpiCard
            color="green"
            icon={CheckCircle2}
            label="Ativos"
            value={kpis.active}
          />
          <KpiCard
            color="blue"
            icon={Wallet}
            label="Saldo total"
            value={formatBRL(kpis.totalBalance)}
          />
        </ListPageKpis>
      )}

      {/* ================================================================ */}
      {/* Barra de filtros rápidos (helper)                                  */}
      {/* ================================================================ */}
      <ListPageFiltersBar
        onMoreClick={() => setIsMoreFiltersOpen(true)}
        moreCount={advancedFilterCount}
        isFetching={isFetching}
      >
        {/* Busca textual */}
        <div className="relative">
          <Search className="h-3 w-3 sm:h-3.5 sm:w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Buscar por nome, e-mail, slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn(
              LIST_FILTERS_INPUT_CLASS,
              'pl-7 w-[160px] sm:w-[260px]',
            )}
          />
        </div>

        {/* Status — Select inline (padrão CrmDeals) */}
        <Select
          value={status}
          onValueChange={(v) => setStatus(v as AffiliateStatus | 'all')}
        >
          <SelectTrigger
            className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[140px]')}
          >
            <CircleDot className="h-3 w-3 mr-1 inline-block" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {/* Mantém o nome do filtro visível no trigger (padrão TaskList) */}
            <SelectItem value="all">Status</SelectItem>
            {(Object.keys(STATUS_LABEL) as AffiliateStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                <span className="inline-flex items-center gap-2">
                  <span
                    className={cn(
                      'inline-block h-2 w-2 rounded-full',
                      STATUS_DOT[s],
                    )}
                  />
                  {STATUS_LABEL[s]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Programa — Select inline (oculto em modo embedded) */}
        {!embedded && (
          <Select value={programId} onValueChange={(v) => setProgramId(v)}>
            <SelectTrigger
              className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[160px]')}
            >
              <Award className="h-3 w-3 mr-1 inline-block" />
              <SelectValue placeholder="Programa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Programa</SelectItem>
              {programs.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </ListPageFiltersBar>

      {/* ================================================================ */}
      {/* Tabela full-bleed (helper)                                        */}
      {/* ================================================================ */}
      <ListPageContent fullBleed>
        {isError ? (
          <div className="p-6 text-sm text-destructive">
            Erro ao carregar afiliados. Tente recarregar a página.
          </div>
        ) : (
          <>
            <AffiliatesTableView
              affiliates={paginatedAffiliates}
              isLoading={isLoading}
              onOpenDetails={openDetails}
            />
            {/* v3.3.0: sentinela do scroll infinito (apenas mobile) */}
            {isMobile && !isLoading && (
              <InfiniteScrollSentinel
                hasMore={infiniteQuery.hasNextPage}
                isLoading={infiniteQuery.isFetchingNextPage}
                onLoadMore={infiniteQuery.fetchNextPage}
              />
            )}
          </>
        )}
      </ListPageContent>

      {/* ================================================================ */}
      {/* Footer com paginação (server-side: totalItems vem do RPC)         */}
      {/* v3.3.0: ocultado no mobile — lá vale o scroll infinito             */}
      {/* ================================================================ */}
      {!isMobile && (
        <ListPageFooter>
          <PaginationFooter
            totalItems={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[10, 20, 50, 100]}
            borderClassName=""
            className="mt-0 pt-0"
          />
        </ListPageFooter>
      )}

      {/* ================================================================ */}
      {/* Sheet de filtros avançados ("Mais")                                */}
      {/* ================================================================ */}
      <Sheet open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="p-4 border-b">
            <SheetTitle>Filtros avançados</SheetTitle>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="p-4 space-y-5">
              {/* Toggle: apenas com saldo > 0 */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="only-balance" className="text-sm">
                    Apenas com saldo &gt; 0
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Mostra somente afiliados com saldo disponível.
                  </p>
                </div>
                <Switch
                  id="only-balance"
                  checked={onlyWithBalance}
                  onCheckedChange={setOnlyWithBalance}
                />
              </div>

              <Separator />

              {/* Faixa de cadastro */}
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-1.5">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  Cadastro
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="created-from" className="text-xs text-muted-foreground">
                      De
                    </Label>
                    <Input
                      id="created-from"
                      type="date"
                      value={createdFrom}
                      onChange={(e) => setCreatedFrom(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="created-to" className="text-xs text-muted-foreground">
                      Até
                    </Label>
                    <Input
                      id="created-to"
                      type="date"
                      value={createdTo}
                      onChange={(e) => setCreatedTo(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>

          <div className="p-4 border-t flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAdvancedFilters}
              disabled={advancedFilterCount === 0}
            >
              Limpar
            </Button>
            <Button size="sm" onClick={() => setIsMoreFiltersOpen(false)}>
              Aplicar
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Drawer de detalhes do afiliado */}
      <ReferralAffiliateDetailSheet
        affiliateId={selectedId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        // v1.3.0: passa as adesões da pessoa selecionada para habilitar a aba "Programas"
        memberships={
          paginatedAffiliates.find((a) => a.id === selectedId)?.memberships
        }
      />

      {/* Modal de cadastro administrativo */}
      <AffiliateAdminCreateModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultProgramId={embeddedProgramId}
      />

      {/* Wizard de importação de afiliados via CSV */}
      <ImportAffiliatesWizard
        open={importOpen}
        onOpenChange={setImportOpen}
        defaultProgramId={embeddedProgramId}
      />
    </ListPageLayout>
  );
}
